import { useEffect, useRef } from "react";

/**
 * Animation de l'en-tête de la page Portefeuilles, sur téléphone uniquement (sous 640 px) :
 * un anneau de répartition qui dépasse du coin en haut à droite, tourne lentement,
 * et dont les parts varient doucement (comme la composition d'un portefeuille).
 * Uniquement des formes et des couleurs : aucun chiffre, pour ne pas faire croire à des données réelles.
 * Sur ordinateur, c'est la constellation (ConstellationBackground) qui s'affiche.
 * Le parent doit être en `position: relative`.
 */
const COLORS = ["0,191,99", "177,117,255", "247,152,55", "0,191,99", "177,117,255"];
const WEIGHTS = [0.34, 0.24, 0.18, 0.14, 0.1];

export default function PortfoliosHeroMobile() {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return undefined;
    const ctx = canvas.getContext("2d");
    const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    let width = 0;
    let height = 0;
    let t = 0;
    let last = 0;
    let frame = 0;

    const resize = () => {
      const rect = canvas.getBoundingClientRect();
      const dpr = window.devicePixelRatio || 1;
      width = rect.width;
      height = rect.height;
      canvas.width = width * dpr;
      canvas.height = height * dpr;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };

    // Un anneau découpé en parts ; les parts « respirent » un peu, l'anneau tourne
    const ring = (cx, cy, r, lw, rot, alpha, phase) => {
      const w = WEIGHTS.map((b, i) => b * (1 + 0.28 * Math.sin(t * 0.45 + i * 1.7 + phase)));
      const sum = w.reduce((a, b) => a + b, 0);
      const gap = 0.05;
      let a = rot;
      w.forEach((v, i) => {
        const span = (v / sum) * Math.PI * 2;
        ctx.beginPath();
        ctx.arc(cx, cy, r, a + gap / 2, a + span - gap / 2);
        ctx.strokeStyle = `rgba(${COLORS[i]},${alpha})`;
        ctx.lineWidth = lw;
        ctx.lineCap = "round";
        ctx.stroke();
        a += span;
      });
    };

    const draw = (dt) => {
      t += dt;
      ctx.clearRect(0, 0, width, height);
      if (!width) return;
      // centre juste au-dessus du coin : l'anneau occupe le coin libre, au-dessus du titre
      const cx = width - 2;
      const cy = -4;
      ring(cx, cy, 74, 10, t * 0.12, 0.8, 0); // grand anneau
      ring(cx, cy, 52, 3, -t * 0.18 + 1, 0.5, 2); // anneau fin, sens inverse
      // repère discret autour
      ctx.beginPath();
      ctx.arc(cx, cy, 96, 0, Math.PI * 2);
      ctx.strokeStyle = "rgba(255,255,255,0.05)";
      ctx.lineWidth = 1;
      ctx.stroke();
    };

    const tick = (now) => {
      const dt = Math.min(0.1, (now - (last || now)) / 1000);
      last = now;
      draw(dt);
      frame = requestAnimationFrame(tick);
    };

    resize();
    draw(0);
    if (!reduceMotion) frame = requestAnimationFrame(tick);
    const observer = new ResizeObserver(() => {
      resize();
      draw(0);
    });
    observer.observe(canvas);
    return () => {
      cancelAnimationFrame(frame);
      observer.disconnect();
    };
  }, []);

  // l'anneau s'efface en s'éloignant du coin, pour ne pas gêner le texte
  const mask = "radial-gradient(210px 170px at 100% 0%, #000 55%, transparent 100%)";
  return (
    <canvas
      ref={canvasRef}
      aria-hidden="true"
      className="sm:hidden absolute inset-0 w-full h-full pointer-events-none"
      style={{ WebkitMaskImage: mask, maskImage: mask }}
      data-testid="portfolios-hero-mobile"
    />
  );
}
